import PropTypes from 'prop-types'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

// Google reCAPTCHA, loaded on demand — no npm dependency. Two flavours, chosen by the backend
// (GET /v1/public/registrations/config) to match the key type created in Google's console:
//   - v2 checkbox ("recaptcha"): <Recaptcha/> below renders the "I'm not a robot" box.
//   - v3 invisible ("recaptcha_v3"): no widget; executeRecaptchaV3() mints a scored token at submit.
// A key of one type cannot be used as the other — Google shows "Invalid key type".
const ONLOAD = '__adasRecaptchaReady'
const V2_URL = `https://www.google.com/recaptcha/api.js?onload=${ONLOAD}&render=explicit`
const v3Url = (siteKey) => `https://www.google.com/recaptcha/api.js?onload=${ONLOAD}&render=${encodeURIComponent(siteKey)}`
export const RECAPTCHA_V3_ACTION = 'signup'

// One script per page, shared by every caller.
const scripts = new Map()

const loadScript = (url) => {
  if (scripts.has(url)) return scripts.get(url)
  const promise = new Promise((resolve, reject) => {
    window[ONLOAD] = () => resolve(window.grecaptcha)
    const script = document.createElement('script')
    script.src = url
    script.async = true
    script.defer = true
    script.onerror = () => {
      scripts.delete(url)
      reject(new Error('reCAPTCHA failed to load'))
    }
    document.head.appendChild(script)
  })
  scripts.set(url, promise)
  return promise
}

export const loadRecaptchaScript = () => (window.grecaptcha?.render ? Promise.resolve(window.grecaptcha) : loadScript(V2_URL))

/** v3: load early (Google scores the whole visit, not just the submit click). */
export const loadRecaptchaV3 = (siteKey) => loadScript(v3Url(siteKey))

/** v3: mint a fresh token for the sign-up action. Tokens live two minutes, so call this at submit. */
export const executeRecaptchaV3 = async (siteKey, action = RECAPTCHA_V3_ACTION) => {
  const grecaptcha = await loadRecaptchaV3(siteKey)
  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(siteKey, { action }).then(resolve, reject)
    })
  })
}

// Test-only: forget the cached loaders between tests.
export const __resetRecaptchaLoader = () => {
  scripts.clear()
}

/**
 * Renders the checkbox and reports the response token through `onChange` (null when it expires
 * or errors). Tokens are single-use and valid for two minutes, so the parent calls `reset()`
 * via the ref after any failed submission to get a fresh one.
 */
export const Recaptcha = forwardRef(({ siteKey, onChange, theme = 'dark' }, ref) => {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const onChangeRef = useRef(onChange)
  const [loadError, setLoadError] = useState(false)
  onChangeRef.current = onChange

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.grecaptcha?.reset) window.grecaptcha.reset(widgetIdRef.current)
      onChangeRef.current?.(null)
    }
  }))

  useEffect(() => {
    let cancelled = false
    loadRecaptchaScript()
      .then((grecaptcha) => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return
        widgetIdRef.current = grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onChangeRef.current?.(token),
          'expired-callback': () => onChangeRef.current?.(null),
          'error-callback': () => onChangeRef.current?.(null)
        })
      })
      .catch(() => !cancelled && setLoadError(true))
    return () => {
      cancelled = true
    }
  }, [siteKey, theme])

  return (
    <div className='flex flex-col items-center'>
      <div ref={containerRef} data-testid='recaptcha' />
      {loadError && (
        <div role='alert' className='mt-2 text-center text-[11px] text-red-300'>
          The “I’m not a robot” check couldn’t load. Check your connection or disable content blockers, then reload the page.
        </div>
      )}
    </div>
  )
})
Recaptcha.displayName = 'Recaptcha'
Recaptcha.propTypes = {
  siteKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(['dark', 'light'])
}
